import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, AlertTriangle } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { useApiError } from '@/lib/useApiError';
import { useCulturalMomentGaps } from '@/queries/phase2';
import type { CulturalMomentGap } from '@/schemas/admin/phase2/culturalMoments';
import { CulturalMomentForm } from '@/features/phase2/CulturalMomentForm';
import { CulturalMomentTagger } from '@/features/phase2/CulturalMomentTagger';
import { useUiStore } from '@/stores/ui';

export const Route = createFileRoute('/_authed/phase2/cultural-moments')({
  component: CulturalMomentsScreen,
});

function CulturalMomentsScreen() {
  const { t } = useTranslation('phase2');
  const gaps = useCulturalMomentGaps();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);
  const [creating, setCreating] = React.useState(false);
  const [tagging, setTagging] = React.useState<CulturalMomentGap['moment'] | null>(null);

  const columns: readonly DataColumn<CulturalMomentGap>[] = [
    {
      id: 'name',
      header: t('moments.col.name', { defaultValue: 'Name' }),
      cell: (r) => {
        const name =
          (locale === 'eg-ar' ? r.moment.name_eg_ar : locale === 'ar' ? r.moment.name_ar : r.moment.name_en) ??
          r.moment.name_en ??
          r.moment.key;
        return (
          <div className="space-y-0.5">
            <p className="font-medium">{name}</p>
            <p className="font-mono text-xs text-[color:var(--color-fg-muted)]">
              <span dir="ltr">
                {r.moment.key} • {r.moment.year}
              </span>
            </p>
          </div>
        );
      },
      sortBy: (r) => `${r.moment.year}-${r.moment.key}`,
    },
    {
      id: 'starts',
      header: t('moments.col.starts', { defaultValue: 'Starts on' }),
      cell: (r) => <DateTime value={r.moment.starts_on} relative={false} />,
      sortBy: (r) => new Date(r.moment.starts_on),
    },
    {
      id: 'until',
      header: t('moments.col.daysUntilStart', { defaultValue: 'Days until' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.moment.days_until_start}
        </span>
      ),
      sortBy: (r) => r.moment.days_until_start,
    },
    {
      id: 'assets',
      header: t('moments.col.assets', { defaultValue: 'Assets' }),
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span dir="ltr" className="font-mono text-xs">
            {r.assets_have} / {r.assets_goal}
          </span>
          {r.gap > 0 ? (
            <Badge tone="warn">
              <AlertTriangle aria-hidden className="size-3.5" />
              {t('moments.gapBadge', { defaultValue: '−{n}', n: r.gap })}
            </Badge>
          ) : (
            <Badge tone="success">{t('moments.metBadge', { defaultValue: 'Met' })}</Badge>
          )}
        </div>
      ),
      sortBy: (r) => -r.gap,
    },
    {
      id: 'warn',
      header: t('moments.col.warn', { defaultValue: 'Prep window' }),
      cell: (r) =>
        r.warn_preparation ? (
          <Badge tone="danger">{t('moments.inWindow', { defaultValue: 'In prep window' })}</Badge>
        ) : (
          <Badge tone="neutral">{t('moments.outsideWindow', { defaultValue: 'Outside window' })}</Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('moments.col.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <Button size="sm" variant="secondary" onClick={() => setTagging(r.moment)}>
          {t('moments.tagContent', { defaultValue: 'Tag content' })}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('moments.title', { defaultValue: 'Cultural moments' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('moments.subtitle', {
              defaultValue:
                'Editorial calendar. Hijri-based moments are upserted yearly on (key, year). The gaps queue shows upcoming moments behind their asset goal.',
            })}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {t('moments.new', { defaultValue: 'New moment' })}
        </Button>
      </header>

      <Tabs defaultValue="gaps">
        <TabsList>
          <TabsTrigger value="gaps">
            {t('moments.tabs.gaps', { defaultValue: 'Gaps queue' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="gaps">
          <Card>
            <CardContent className="pt-6">
              <DataTable<CulturalMomentGap>
                data={gaps.data ?? []}
                columns={columns}
                rowKey={(r) => r.moment.id}
                loading={gaps.isLoading}
                error={gaps.error ? resolveError(gaps.error) : null}
                empty={{
                  title: t('moments.empty', { defaultValue: 'No upcoming moments need prep.' }),
                  body: t('moments.emptyBody', {
                    defaultValue:
                      "You're either ahead of schedule or there are no active moments. Create one to plan ahead.",
                  }),
                }}
                initialSort={{ id: 'until', direction: 'asc' }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('moments.newTitle', { defaultValue: 'New cultural moment' })}</DialogTitle>
            <DialogDescription>
              {t('moments.newBody', {
                defaultValue:
                  'Idempotent on (key, year). Re-saving the same key/year combination updates the existing row in place — tagged content stays linked.',
              })}
            </DialogDescription>
          </DialogHeader>
          <CulturalMomentForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(tagging)} onOpenChange={(o) => !o && setTagging(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('moments.taggerTitle', { defaultValue: 'Tag content' })}</DialogTitle>
            <DialogDescription>
              {t('moments.taggerBody', {
                defaultValue:
                  'Attach any post/story/event/group/prompt/product to this moment. Idempotent — re-tagging is a no-op.',
              })}
            </DialogDescription>
          </DialogHeader>
          {tagging ? (
            <CulturalMomentTagger
              momentId={tagging.id}
              momentLabel={
                <span dir="ltr" className="font-mono">
                  {tagging.key} {tagging.year}
                </span>
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
